import {
	OrderQuoteGuestAccessTokenStatus,
	OrderQuoteOfferStatus,
	OrderQuoteStatus,
	OrderStatus,
	OrderItemStatus,
	OrderSampleStatus,
	OrderShippingMethod,
	OrderShippingState,
	StatusEnum,
} from '@codezo/graphql';
import { formatDate } from './utils';

export type StatusTone = 'success' | 'warn' | 'info' | 'neutral' | 'danger';

/* ── Shipping method ── */

/** Display labels for delivery/shipping methods, keyed by the enum. */
export const SHIPPING_METHOD_LABEL: Record<OrderShippingMethod, string> = {
	[OrderShippingMethod.Express]: 'Express',
	[OrderShippingMethod.Standard]: 'Standard',
};

export interface StatusConfig {
	label: string;
	tone: StatusTone;
}

/* ── Order Quote ── */

export const ORDER_QUOTE_STATUS: Record<string, StatusConfig> = {
	[OrderQuoteStatus.Draft]: { label: 'Draft', tone: 'neutral' },
	[OrderQuoteStatus.Requested]: { label: 'Requested', tone: 'info' },
	[OrderQuoteStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderQuoteStatus.PendingReview]: { label: 'Pending review', tone: 'warn' },
	[OrderQuoteStatus.Submitted]: { label: 'Submitted', tone: 'info' },
	[OrderQuoteStatus.Accepted]: { label: 'Accepted', tone: 'success' },
	[OrderQuoteStatus.Rejected]: { label: 'Rejected', tone: 'danger' },
	[OrderQuoteStatus.Expired]: { label: 'Expired', tone: 'neutral' },
};

/* ── Order Quote Offer ── */

export const ORDER_QUOTE_OFFER_STATUS: Record<string, StatusConfig> = {
	[OrderQuoteOfferStatus.Draft]: { label: 'Draft', tone: 'neutral' },
	[OrderQuoteOfferStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderQuoteOfferStatus.PendingReview]: { label: 'Pending review', tone: 'warn' },
	[OrderQuoteOfferStatus.Submitted]: { label: 'Submitted', tone: 'info' },
	[OrderQuoteOfferStatus.Accepted]: { label: 'Accepted', tone: 'success' },
	[OrderQuoteOfferStatus.Rejected]: { label: 'Rejected', tone: 'danger' },
	[OrderQuoteOfferStatus.Cancelled]: { label: 'Cancelled', tone: 'neutral' },
	[OrderQuoteOfferStatus.Expired]: { label: 'Expired', tone: 'neutral' },
};

/* ── Guest acceptance link ── */

export const GUEST_ACCESS_TOKEN_STATUS: Record<string, StatusConfig> = {
	[OrderQuoteGuestAccessTokenStatus.Active]: { label: 'Active', tone: 'info' },
	[OrderQuoteGuestAccessTokenStatus.Accepted]: { label: 'Accepted', tone: 'success' },
	[OrderQuoteGuestAccessTokenStatus.Revoked]: { label: 'Revoked', tone: 'neutral' },
};

/* ── Order ── */

export const ORDER_STATUS: Record<string, StatusConfig> = {
	[OrderStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderStatus.InProduction]: { label: 'In Production', tone: 'info' },
	[OrderStatus.Completed]: { label: 'Completed', tone: 'success' },
	[OrderStatus.Cancelled]: { label: 'Cancelled', tone: 'danger' },
};

/* ── Order shipping (orders list delivery column) ── */

export const ORDER_SHIPPING_STATE: Record<OrderShippingState, StatusConfig> = {
	[OrderShippingState.NoShipments]: { label: 'No shipments', tone: 'neutral' },
	[OrderShippingState.NotShipped]: { label: 'Not shipped', tone: 'neutral' },
	[OrderShippingState.ReadyToShip]: { label: 'Ready to ship', tone: 'warn' },
	[OrderShippingState.InTransit]: { label: 'In transit', tone: 'info' },
	[OrderShippingState.PartiallyDelivered]: { label: 'Partially delivered', tone: 'info' },
	[OrderShippingState.Delivered]: { label: 'Delivered', tone: 'success' },
};

/* ── Order Item ── */

export const ORDER_ITEM_STATUS: Record<string, StatusConfig> = {
	[OrderItemStatus.Pending]: { label: 'Pending', tone: 'neutral' },
	[OrderItemStatus.InProduction]: { label: 'In Production', tone: 'warn' },
	[OrderItemStatus.ProductionCompleted]: { label: 'Production Completed', tone: 'info' },
	[OrderItemStatus.PartiallyShipped]: { label: 'Partially Shipped', tone: 'warn' },
	[OrderItemStatus.Shipped]: { label: 'Shipped', tone: 'info' },
	[OrderItemStatus.Delivered]: { label: 'Delivered', tone: 'success' },
	[OrderItemStatus.Cancelled]: { label: 'Cancelled', tone: 'danger' },
};

/* ── Sample ── */

export const SAMPLE_STATUS: Record<string, StatusConfig> = {
	[OrderSampleStatus.Pending]: { label: 'Pending', tone: 'warn' },
	[OrderSampleStatus.InProduction]: { label: 'In Production', tone: 'warn' },
	[OrderSampleStatus.ProductionCompleted]: { label: 'Production Completed', tone: 'info' },
	[OrderSampleStatus.Shipped]: { label: 'Shipped', tone: 'info' },
	[OrderSampleStatus.VirtualSubmitted]: { label: 'Virtual Submitted', tone: 'info' },
	[OrderSampleStatus.AwaitingApproval]: { label: 'Awaiting Approval', tone: 'info' },
	[OrderSampleStatus.Approved]: { label: 'Approved', tone: 'success' },
	[OrderSampleStatus.Rejected]: { label: 'Rejected', tone: 'danger' },
	[OrderSampleStatus.Cancelled]: { label: 'Cancelled', tone: 'neutral' },
};

/* ── Helper ── */

/* ── Client contact ── */

/**
 * A client contact's badge is derived from the *pair* (status, isGuest), never
 * from `status` alone. The two are orthogonal: `status` is account lifecycle
 * (PENDING = invited, not activated), `isGuest` is provenance (came in through
 * a quote acceptance link, password-less, not yet converted to a full account).
 *
 * A guest is PENDING by design and may sit there indefinitely while actively
 * reviewing an offer, so labelling them as merely waiting reads as though we
 * are waiting on them — and pairing that with a separate "Guest" badge reads as
 * a contradiction. One derived label says it once, correctly.
 *
 * Non-guest PENDING splits on `invitedAt`, because since `sendInvitation: false`
 * exists that one status covers two situations: "invited, hasn't accepted" and
 * "recorded, never emailed". Status alone can't tell them apart — both are
 * PENDING — so calling either one "Invited" asserts an email that may never
 * have been sent.
 */
export function clientContactStatus(
	status: StatusEnum,
	isGuest: boolean,
	invitedAt?: string | null
): StatusConfig {
	if (status === StatusEnum.Canceled) return { label: 'Canceled', tone: 'danger' };
	if (status === StatusEnum.Completed) {
		// Converting a guest is meant to clear isGuest, so this pair is an
		// anomaly worth surfacing rather than hiding behind a plain "Active".
		return isGuest
			? { label: 'Active (guest)', tone: 'success' }
			: { label: 'Active', tone: 'success' };
	}
	if (isGuest) return { label: 'Guest access', tone: 'info' };
	// The date rides in the badge rather than a column of its own: it is only
	// meaningful for this one state, so a column would be empty on most rows.
	if (invitedAt) return { label: `Invited ${formatDate(invitedAt)}`, tone: 'warn' };
	// Neutral, not warn: nobody is waiting on this person yet — we haven't asked.
	return { label: 'Not invited', tone: 'neutral' };
}

/* ── Vetter user ── */

/**
 * A vetter's invitation state, from `invitedAt` alone.
 *
 * Deliberately narrower than `clientContactStatus`: VetterUser carries no
 * status field, so we know whether we emailed them but not whether they ever
 * accepted. This says only what the data supports — "Invited" means the email
 * went out, never that the account is live. Give VetterUser a status and this
 * can gain an "Active" state like contacts have.
 */
export function vetterInvitationStatus(invitedAt?: string | null): StatusConfig {
	if (invitedAt) return { label: `Invited ${formatDate(invitedAt)}`, tone: 'warn' };
	// Neutral, not warn: nobody is waiting on them yet — we haven't asked.
	return { label: 'Not invited', tone: 'neutral' };
}
